import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

/**
 * VisualBLS Component - UPGRADED FOR SOMATIC EFFECTIVENESS
 * 
 * A large, immersive bilateral stimulation animation.
 * Shows a soft orb moving slowly left-right across the screen.
 * Used for self-guided EMDR-style visual tracking.
 * 
 * Design principles:
 * - Large visuals (60-75% screen width) for embodied tracking
 * - Slow, predictable pacing (8-12 seconds per cycle)
 * - Soft colors with gentle glow effects
 * - Speed slider for user control
 * - Non-directive, warm instructional text
 * - Reduced motion & text-only accessibility options
 */

export const VisualBLS = ({
    duration = 5, // Total duration in seconds for one complete left-right-left cycle (faster)
    cycles = 3, // Number of complete cycles
    size = 'lg', // 'sm' | 'md' | 'lg' | 'xl' | 'immersive'
    variant = 'sage', // 'sage' | 'blue' | 'lavender'
    autoStart = true,
    onComplete,
    onSkip,
    showInstructions = true,
    showSpeedControl = false, // Show speed slider
    textOnly = false, // Accessibility: text-only mode
    className,
}) => {
    const [isActive, setIsActive] = useState(autoStart);
    const [currentCycle, setCurrentCycle] = useState(0);
    const [position, setPosition] = useState(50); // Percentage position (0-100)
    const [direction, setDirection] = useState('right'); // 'left' | 'right'
    const [speed, setSpeed] = useState('medium'); // 'slow' | 'medium' | 'fast'
    
    // Check for reduced motion preference
    const prefersReducedMotion = typeof window !== 'undefined' && 
        (localStorage.getItem('reducedMotion') === 'true' ||
         window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    // Speed configurations (duration in seconds) - faster options
    const speedConfig = {
        slow: 6,
        medium: 4,
        fast: 2.5,
    };

    const actualDuration = showSpeedControl ? speedConfig[speed] : duration;

    // UPGRADED size configurations - PERFECTLY CENTERED, FULL WIDTH
    // Movement path: 80-90% of screen width, centered on screen
    // Isolated container with generous spacing
    const sizeConfig = {
        sm: {
            container: 'py-8',
            track: 'h-14',
            trackWidth: '75vw',
            maxTrackWidth: '500px',
            orb: 'w-10 h-10',
            glow: 'w-20 h-20',
            outerGlow: 'w-28 h-28',
            positionBounds: { min: 5, max: 95 },
        },
        md: {
            container: 'py-10',
            track: 'h-16',
            trackWidth: '80vw',
            maxTrackWidth: '800px',
            orb: 'w-12 h-12',
            glow: 'w-24 h-24',
            outerGlow: 'w-32 h-32',
            positionBounds: { min: 4, max: 96 },
        },
        lg: {
            container: 'py-12',
            track: 'h-18',
            trackWidth: '85vw',
            maxTrackWidth: '1100px',
            orb: 'w-14 h-14',
            glow: 'w-28 h-28',
            outerGlow: 'w-40 h-40',
            positionBounds: { min: 3, max: 97 },
        },
        xl: {
            container: 'py-14',
            track: 'h-20',
            trackWidth: '88vw',
            maxTrackWidth: '1300px',
            orb: 'w-16 h-16',
            glow: 'w-32 h-32',
            outerGlow: 'w-44 h-44',
            positionBounds: { min: 2, max: 98 },
        },
        immersive: {
            container: 'py-16',
            track: 'h-24',
            trackWidth: '90vw',
            maxTrackWidth: '1500px',
            orb: 'w-20 h-20',
            glow: 'w-40 h-40',
            outerGlow: 'w-52 h-52',
            positionBounds: { min: 2, max: 98 },
        },
    };

    // Soft gradient color configurations
    const colorConfig = {
        sage: {
            orb: 'bg-gradient-radial from-[#1D6B6B] via-[#8FAF85] to-[#7D8E95]',
            glow: 'bg-[#7D8E95]/50',
            outerGlow: 'bg-[#7D8E95]/20',
            track: 'bg-gradient-to-r from-[#7D8E95]/5 via-[#7D8E95]/10 to-[#7D8E95]/5',
            trackBorder: 'border-[#7D8E95]/15',
            text: 'text-[#1D6B6B]',
            active: 'bg-[#1D6B6B]',
            inactive: 'bg-[#7D8E95]/30',
        },
        blue: {
            orb: 'bg-gradient-radial from-[#7D8E95] via-[#8AACBC] to-[#7D8E95]',
            glow: 'bg-[#7D8E95]/60',
            outerGlow: 'bg-[#7D8E95]/25',
            track: 'bg-gradient-to-r from-[#7D8E95]/5 via-[#7D8E95]/12 to-[#7D8E95]/5',
            trackBorder: 'border-[#7D8E95]/20',
            text: 'text-[#7D8E95]',
            active: 'bg-[#7D8E95]',
            inactive: 'bg-[#7D8E95]/30',
        },
        lavender: {
            orb: 'bg-gradient-radial from-[#7D8E95] via-[#A99AB2] to-[#7D8E95]',
            glow: 'bg-[#7D8E95]/50',
            outerGlow: 'bg-[#7D8E95]/20',
            track: 'bg-gradient-to-r from-[#7D8E95]/5 via-[#7D8E95]/10 to-[#7D8E95]/5',
            trackBorder: 'border-[#7D8E95]/15',
            text: 'text-[#7D8E95]',
            active: 'bg-[#7D8E95]',
            inactive: 'bg-[#7D8E95]/30',
        },
    };

    const s = sizeConfig[size];
    const c = colorConfig[variant];

    // Animation logic - smoother, slower movement with full-width travel
    useEffect(() => {
        if (!isActive || prefersReducedMotion) return;

        const halfCycleDuration = (actualDuration / 2) * 1000;
        const stepInterval = 20; // Smoother animation (~50fps)
        const steps = halfCycleDuration / stepInterval;
        
        // Use position bounds from size config for wider travel
        const { min: minPos, max: maxPos } = s.positionBounds;
        const travelDistance = maxPos - minPos; // e.g., 96 - 2 = 94% travel
        const stepSize = travelDistance / steps;

        let animationFrame;
        let stepCount = 0;
        let currentDirection = direction;

        const animate = () => {
            stepCount++;
            
            setPosition(prev => {
                let newPos;
                if (currentDirection === 'right') {
                    newPos = minPos + (stepSize * stepCount);
                    if (newPos >= maxPos) {
                        currentDirection = 'left';
                        setDirection('left');
                        stepCount = 0;
                    }
                } else {
                    newPos = maxPos - (stepSize * stepCount);
                    if (newPos <= minPos) {
                        currentDirection = 'right';
                        setDirection('right');
                        stepCount = 0;
                        setCurrentCycle(c => {
                            const newCycle = c + 1;
                            if (newCycle >= cycles && onComplete) {
                                setIsActive(false);
                                onComplete();
                            }
                            return newCycle;
                        });
                    }
                }
                return Math.max(minPos, Math.min(maxPos, newPos));
            });

            if (isActive) {
                animationFrame = requestAnimationFrame(animate);
            }
        };

        animationFrame = requestAnimationFrame(animate);

        return () => {
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [isActive, direction, actualDuration, cycles, onComplete, prefersReducedMotion]);

    const handleSkip = useCallback(() => {
        setIsActive(false);
        if (onSkip) onSkip();
    }, [onSkip]);

    const handleToggle = useCallback(() => {
        setIsActive(prev => !prev);
    }, []);

    // Text-only mode for accessibility
    if (textOnly) {
        return (
            <div 
                className={cn(
                    'flex flex-col items-center justify-center p-8 rounded-2xl bg-[#FBE0C3]/50',
                    className
                )}
                data-testid="visual-bls-text"
            >
                <p className={cn('text-xl font-medium mb-4', c.text)}>
                    {isActive 
                        ? `Look ${direction === 'right' ? 'right' : 'left'}...` 
                        : 'Visual tracking paused'
                    }
                </p>
                <p className="text-base text-[#2A8585] mb-2">
                    Cycle {currentCycle + 1} of {cycles}
                </p>
                <p className="text-sm text-[#7D8E95] mt-4">
                    Gently move your eyes left and right
                </p>
                <div className="flex gap-4 mt-6">
                    <button
                        onClick={handleToggle}
                        className="text-sm text-[#7D8E95] hover:text-[#2A8585] transition-colors underline"
                    >
                        {isActive ? 'Pause' : 'Continue'}
                    </button>
                    {onSkip && (
                        <button
                            onClick={handleSkip}
                            className="text-sm text-[#7D8E95] hover:text-[#2A8585] transition-colors underline"
                        >
                            Skip
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Reduced motion mode - static indicators
    if (prefersReducedMotion) {
        return (
            <div 
                className={cn(
                    'flex flex-col items-center justify-center gap-6',
                    className
                )}
                data-testid="visual-bls"
            >
                {showInstructions && (
                    <p className="text-base text-[#2A8585] text-center max-w-sm leading-relaxed">
                        Gently shift your gaze from left to right and back again.
                    </p>
                )}
                <div className="flex items-center gap-8">
                    <div className={cn(
                        'w-4 h-4 rounded-full transition-opacity duration-500',
                        direction === 'left' ? c.active : c.inactive
                    )} />
                    <span className="text-sm text-[#7D8E95]">←  →</span>
                    <div className={cn(
                        'w-4 h-4 rounded-full transition-opacity duration-500',
                        direction === 'right' ? c.active : c.inactive
                    )} />
                </div>
                <p className="text-sm text-[#7D8E95]">
                    Cycle {currentCycle + 1} of {cycles}
                </p>
            </div>
        );
    }

    return (
        /* 
         * ISOLATED EMDR CONTAINER
         * - Full viewport width for proper centering relative to screen
         * - Fixed positioning context to prevent layout shifts
         * - Generous vertical spacing for visual separation
         */
        <div 
            className={cn(
                // Isolated full-width container
                'relative w-screen left-1/2 -translate-x-1/2',
                // Centering rules
                'flex flex-col items-center justify-center',
                // Vertical spacing
                s.container,
                // Prevent layout influence
                'overflow-visible box-border',
                className
            )}
            style={{
                marginLeft: 'auto',
                marginRight: 'auto',
                textAlign: 'center',
            }}
            data-testid="visual-bls"
        >
            {/* Instructions - warm and non-directive */}
            {showInstructions && (
                <p className="text-lg text-[#2A8585] text-center max-w-md leading-relaxed mb-8 px-4">
                    Let your eyes gently follow the light. There's no rush.
                </p>
            )}

            {/* 
             * TRACK CONTAINER - Centered relative to SCREEN
             * - Symmetrical left/right endpoints
             * - Does not affect surrounding layout
             */}
            <div 
                className={cn(
                    'relative rounded-full flex items-center justify-center border',
                    s.track,
                    c.track,
                    c.trackBorder
                )}
                style={{
                    width: s.trackWidth,
                    maxWidth: s.maxTrackWidth,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                }}
                onClick={handleToggle}
                role="button"
                aria-label={isActive ? 'Pause visual tracking' : 'Resume visual tracking'}
            >
                {/* Moving orb - smooth, predictable movement */}
                <div 
                    className="absolute flex items-center justify-center pointer-events-none"
                    style={{
                        left: `${position}%`,
                        transform: 'translateX(-50%)',
                        transition: 'none',
                    }}
                >
                    {/* Outer soft glow - even halo */}
                    <div 
                        className={cn(
                            'absolute rounded-full blur-3xl opacity-40',
                            s.outerGlow,
                            c.outerGlow
                        )}
                    />
                    {/* Inner glow */}
                    <div 
                        className={cn(
                            'absolute rounded-full blur-xl opacity-60',
                            s.glow,
                            c.glow
                        )}
                    />
                    {/* Main orb - large and visible */}
                    <div 
                        className={cn(
                            'relative rounded-full shadow-xl',
                            s.orb,
                            c.orb
                        )}
                    />
                </div>

                {/* Symmetrical endpoint indicators */}
                <div className={cn('absolute left-4 w-2 h-2 rounded-full opacity-25', c.inactive)} />
                <div className={cn('absolute right-4 w-2 h-2 rounded-full opacity-25', c.inactive)} />
            </div>

            {/* Speed control slider */}
            {showSpeedControl && (
                <div className="flex items-center gap-4 px-4 mt-8">
                    <span className="text-sm text-[#7D8E95]">Speed:</span>
                    <div className="flex gap-2">
                        {(['slow', 'medium', 'fast']).map((spd) => (
                            <button
                                key={spd}
                                onClick={() => setSpeed(spd)}
                                className={cn(
                                    'px-4 py-2 rounded-full text-sm transition-colors capitalize',
                                    speed === spd 
                                        ? `${c.active} text-white` 
                                        : 'bg-[#FFBB98]/50 text-[#7D8E95] hover:bg-[#FFBB98]'
                                )}
                                data-testid={`speed-${spd}`}
                            >
                                {spd}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Cycle indicator - larger dots with more spacing */}
            <div className="flex items-center gap-4 mt-8">
                {Array.from({ length: cycles }).map((_, i) => (
                    <div 
                        key={i}
                        className={cn(
                            'w-3.5 h-3.5 rounded-full transition-all duration-500',
                            i < currentCycle 
                                ? c.active 
                                : i === currentCycle && isActive
                                    ? `${c.glow} animate-pulse`
                                    : c.inactive
                        )}
                    />
                ))}
            </div>

            {/* Controls - more prominent with spacing */}
            <div className="flex items-center gap-6 mt-6">
                <button
                    onClick={handleToggle}
                    className="text-base text-[#2A8585] hover:text-[#1D6B6B] transition-colors"
                >
                    {isActive ? 'Tap to pause' : 'Tap to continue'}
                </button>
                {onSkip && (
                    <>
                        <span className="text-[#FFBB98]">|</span>
                        <button
                            onClick={handleSkip}
                            className="text-base text-[#7D8E95] hover:text-[#2A8585] transition-colors"
                        >
                            Skip this
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * Compact BLS Indicator
 * 
 * A larger inline indicator for BLS steps
 * Shows alternating left-right dots with clearer visual feedback
 */
export const BLSIndicator = ({
    className,
    variant = 'sage',
    speed = 1000, // ms between alternations
}) => {
    const [activeLeft, setActiveLeft] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveLeft(prev => !prev);
        }, speed);
        return () => clearInterval(interval);
    }, [speed]);

    const colorConfig = {
        sage: { active: 'bg-[#1D6B6B]', inactive: 'bg-[#7D8E95]/25' },
        blue: { active: 'bg-[#7D8E95]', inactive: 'bg-[#7D8E95]/25' },
        lavender: { active: 'bg-[#7D8E95]', inactive: 'bg-[#7D8E95]/25' },
    };

    const c = colorConfig[variant];

    return (
        <div className={cn('inline-flex items-center gap-4', className)}>
            <div 
                className={cn(
                    'w-4 h-4 rounded-full transition-all duration-500',
                    activeLeft ? c.active : c.inactive
                )}
            />
            <span className="text-sm text-[#7D8E95]">←  →</span>
            <div 
                className={cn(
                    'w-4 h-4 rounded-full transition-all duration-500',
                    !activeLeft ? c.active : c.inactive
                )}
            />
        </div>
    );
};

export default VisualBLS;
